import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, Check } from "lucide-react";
import { useAuth } from "@/auth";
import { useT } from "@/i18n";

const inputCls =
  "w-full brutal-border rounded-lg px-4 py-2.5 bg-background text-foreground font-medium outline-none focus:ring-2 focus:ring-primary";
const btnCls =
  "w-full font-display font-bold bg-secondary text-secondary-foreground px-6 py-3 rounded-full brutal-border brutal-shadow hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-none transition-all flex items-center justify-center gap-2 disabled:opacity-60";

export const AuthModal = ({ open, onOpenChange, mode = "login", sessionId, onSuccess }) => {
  const { login, register, forgotPassword } = useAuth();
  const { t } = useT();
  const [screen, setScreen] = useState(mode === "postpay" ? "signup" : mode); // login | signup | forgot
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [sent, setSent] = useState(false);

  const postPay = mode === "postpay";
  const isSignup = postPay || screen === "signup";
  const isForgot = screen === "forgot";

  useEffect(() => {
    if (open) {
      setScreen(mode === "postpay" ? "signup" : mode);
      setErr("");
      setEmail("");
      setPassword("");
      setSent(false);
    }
  }, [open, mode]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      if (isForgot) {
        await forgotPassword(email, window.location.origin);
        setSent(true);
      } else if (isSignup) {
        await register(email, password, postPay ? sessionId : undefined);
        onSuccess && onSuccess();
        onOpenChange(false);
      } else {
        await login(email, password);
        onSuccess && onSuccess();
        onOpenChange(false);
      }
    } catch (e2) {
      setErr(e2.message);
    } finally {
      setBusy(false);
    }
  };

  let title, sub;
  if (postPay) {
    title = t("auth.postPayTitle");
    sub = t("auth.postPaySub");
  } else if (isForgot) {
    title = t("auth.forgotTitle");
    sub = t("auth.forgotSub");
  } else if (isSignup) {
    title = t("auth.signupTitle");
    sub = t("auth.signupSub");
  } else {
    title = t("auth.loginTitle");
    sub = t("auth.loginSub");
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="auth-modal">
        <DialogHeader>
          <DialogTitle className="font-display font-extrabold text-2xl tracking-tight flex items-center gap-2">
            {postPay && <Check className="w-6 h-6 text-accent" />}
            {title}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">{sub}</DialogDescription>
        </DialogHeader>

        {isForgot && sent ? (
          <p className="text-sm font-medium text-foreground mt-2" data-testid="auth-forgot-sent">
            {t("auth.forgotSent")}
          </p>
        ) : (
          <form onSubmit={submit} className="space-y-4 mt-2">
            <div>
              <label className="step-tag text-muted-foreground mb-1 block">{t("auth.email")}</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="auth-email-input"
                autoComplete="email"
                className={inputCls}
                placeholder="you@example.com"
              />
            </div>
            {!isForgot && (
              <div>
                <label className="step-tag text-muted-foreground mb-1 block">{t("auth.password")}</label>
                <input
                  type="password"
                  required
                  minLength={6}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="auth-password-input"
                  autoComplete={isSignup ? "new-password" : "current-password"}
                  className={inputCls}
                  placeholder="••••••••"
                />
              </div>
            )}

            {err && (
              <p className="text-sm font-semibold text-destructive" data-testid="auth-error">
                {err}
              </p>
            )}

            <button type="submit" disabled={busy} data-testid="auth-submit-btn" className={btnCls}>
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              {isForgot ? t("auth.forgotCta") : isSignup ? t("auth.signupCta") : t("auth.loginCta")}
            </button>
          </form>
        )}

        {!postPay && !isForgot && (
          <>
            {screen === "login" && (
              <p className="text-center mt-3">
                <button
                  type="button"
                  data-testid="auth-forgot-btn"
                  onClick={() => {
                    setErr("");
                    setScreen("forgot");
                  }}
                  className="text-sm text-muted-foreground hover:text-primary underline"
                >
                  {t("auth.forgotLink")}
                </button>
              </p>
            )}
            <p className="text-sm text-muted-foreground text-center mt-2">
              {isSignup ? t("auth.haveAccount") : t("auth.noAccount")}{" "}
              <button
                type="button"
                data-testid="auth-switch-btn"
                onClick={() => {
                  setErr("");
                  setScreen(isSignup ? "login" : "signup");
                }}
                className="font-display font-bold text-foreground hover:text-primary underline"
              >
                {isSignup ? t("auth.switchLogin") : t("auth.switchSignup")}
              </button>
            </p>
          </>
        )}

        {isForgot && (
          <p className="text-sm text-muted-foreground text-center mt-3">
            <button
              type="button"
              data-testid="auth-back-login-btn"
              onClick={() => {
                setErr("");
                setSent(false);
                setScreen("login");
              }}
              className="font-display font-bold text-foreground hover:text-primary underline"
            >
              {t("auth.switchLogin")}
            </button>
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const ResetPasswordModal = ({ open, onOpenChange, token, onDone }) => {
  const { resetPassword } = useAuth();
  const { t } = useT();
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) {
      setPassword("");
      setErr("");
      setDone(false);
    }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await resetPassword(token, password);
      setDone(true);
    } catch (e2) {
      setErr(e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="reset-modal">
        <DialogHeader>
          <DialogTitle className="font-display font-extrabold text-2xl tracking-tight">
            {t("auth.resetTitle")}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">{t("auth.resetSub")}</DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="mt-2">
            <p className="text-sm font-medium text-foreground flex items-center gap-2" data-testid="reset-done">
              <Check className="w-5 h-5 text-accent" /> {t("auth.resetDone")}
            </p>
            <button
              type="button"
              data-testid="reset-to-login-btn"
              onClick={() => onDone && onDone()}
              className={btnCls + " mt-4"}
            >
              {t("auth.loginCta")}
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4 mt-2">
            <div>
              <label className="step-tag text-muted-foreground mb-1 block">{t("auth.newPassword")}</label>
              <input
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="reset-password-input"
                autoComplete="new-password"
                className={inputCls}
                placeholder="••••••••"
              />
            </div>
            {err && (
              <p className="text-sm font-semibold text-destructive" data-testid="reset-error">
                {err}
              </p>
            )}
            <button type="submit" disabled={busy} data-testid="reset-submit-btn" className={btnCls}>
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              {t("auth.resetCta")}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
